import type {
  DealerOptionItemDto,
  MachineAttachmentItemDto,
  MachineItemNewPriceDto,
  MachineQuotationItemDto,
  QuotationDto,
  QuotationMarginDto
} from '@api/machines'
import { ApiError, PdfGenerationApiService } from '@api/pdf-generation'
import { PERMISSIONS_KEY, QuoteStatus } from '@common/utils'
import { errorToast } from '@components/reusable'
import { orderBy } from 'lodash'
import type { Dispatch, SetStateAction } from 'react'
import type { CombinedQuotationItems } from '@/pages/quotation-tool/stores/deal-summary/types'
import { AccessDeniedReason, type PdfDownloadTypes } from './constants'
import type {
  NestedQuoteItemsType,
  PdfDownloadButtonsState,
  PermissionDto
} from './types'

type UniqueArray = {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  [key: string]: any
}

export const getUniqueValues = <T extends UniqueArray>(
  array: T[],
  key: keyof T
): T[] => {
  const uniqueObjects: T[] = []
  const uniqueKeys: Set<any> = new Set()

  for (const obj of array) {
    const keyValue = obj[key]

    if (!uniqueKeys.has(keyValue)) {
      uniqueKeys.add(keyValue)
      uniqueObjects.push(obj)
    }
  }

  return uniqueObjects
}

export const formatInputAsInt = (inputString: string) => {
  const stringWithoutCommas = inputString.replace(/,/g, '')

  const numericValue = Number.parseFloat(stringWithoutCommas)

  return numericValue
}

export const formatInputAsCurrency = (inputString: string) => {
  return inputString.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const sanitizeInput = (inputString: string, allowDecimals = false) => {
  if (allowDecimals) {
    return inputString.replace(/[^0-9.]/g, '')
  }

  return inputString.replace(/\D/g, '')
}

export const isActiveRoute = (currentPath: string, routePath: string) =>
  currentPath === routePath || currentPath.includes(routePath)

export const getUserPermissions = () => {
  const permissions = sessionStorage.getItem(PERMISSIONS_KEY)

  if (!permissions) {
    return undefined
  }

  const userPermissions: PermissionDto[] = JSON.parse(permissions)

  return userPermissions
}

export const downloadPdf = async (
  type: PdfDownloadTypes,
  quoteId: string,
  setDlBtnsState: Dispatch<SetStateAction<PdfDownloadButtonsState>>,
  t?: (key: string) => string
) => {
  const handleButtonsState = (activeState: boolean) => {
    setDlBtnsState(prevState => ({
      ...prevState,
      [type]: activeState
    }))
  }

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  let apiCall: any
  switch (type) {
    case 'internalApproval':
      apiCall =
        PdfGenerationApiService.quotationSummaryInternalApprovalPdfGeneration
      break
    case 'salesSupport':
      apiCall =
        PdfGenerationApiService.quotationSummarySalesSupportPdfGeneration
      break
    case 'customer':
      apiCall =
        PdfGenerationApiService.quotationSummaryCustomerQuotePdfGeneration
      break
    default:
      break
  }

  try {
    if (!quoteId) {
      return
    }
    handleButtonsState(true)
    const response = await apiCall?.(quoteId)
    if (response) {
      //@ts-ignore
      const blob = new Blob([response.responseBody], {
        type: 'application/pdf'
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      //@ts-ignore
      a.download = response.responseHeader.split('"')[1]
      document.body.appendChild(a)
      a.click()
      URL.revokeObjectURL(url)
    }
    handleButtonsState(false)
  } catch (err) {
    const error = err instanceof ApiError
    if (error) {
      const errObj = err.body

      if (errObj?.responseBody?.includes("can't be 0 or less")) {
        errorToast(t?.('apiErrorMessages.zeroPriceError') ?? '')
      } else {
        errorToast(t?.('apiErrorMessages.genericPdfError') ?? '')
      }
    }
  } finally {
    handleButtonsState(false)
  }
}

export const filterQuotes = (quotes: QuotationDto[], status: string[]) => {
  return quotes.filter(quote => status.includes(quote.status ?? ''))
}

export const formatCustomerData = (emails: string[]) => {
  return emails.map(email => ({ email }))
}

export const calculateDealerOptionsSectionTotal = (
  items: DealerOptionItemDto[]
) => {
  return items
    .filter(item => !item.isArchived)
    .reduce(
      (acc, currentVal) =>
        acc + (currentVal.quantity ?? 0) * (currentVal.dnp ?? 0),
      0
    )
}

export const quoteHasArchivedItems = (
  machines?: MachineQuotationItemDto[] | null,
  attachments?: MachineAttachmentItemDto[] | null
) => {
  let hasArchivedItems = false

  for (const machine of machines ?? []) {
    if (machine.isArchived || machine.hasItsBrandArchived) {
      hasArchivedItems = true
    }
  }

  for (const attachment of attachments ?? []) {
    if (attachment.isArchived || attachment.hasItsBrandArchived) {
      hasArchivedItems = true
    }
  }

  return hasArchivedItems
}

export const quoteWithCostChangeItems = (
  machines?: MachineQuotationItemDto[] | null,
  attachments?: MachineAttachmentItemDto[] | null
) => {
  let costChangesCount = 0

  for (const machine of machines ?? []) {
    if (machine.priceChanged) {
      costChangesCount++
    }
  }

  for (const attachment of attachments ?? []) {
    if (attachment.priceChanged) {
      costChangesCount++
    }
  }

  return costChangesCount
}

export const denyAccessToSection = (
  quoteMargin: QuotationMarginDto,
  quote: QuotationDto
) => {
  const hasNoMachines =
    quoteMargin.machineItems?.length === 0 || quote.machineItems?.length === 0
  const hasNoAttachments =
    quoteMargin.machineAttachmentItems?.length === 0 ||
    quote.machineAttachmentItems?.length === 0

  const isInNeedsRevisingStatus =
    quoteMargin.status === QuoteStatus.NEEDS_REVISING ||
    quote.status === QuoteStatus.NEEDS_REVISING

  // specifically pass machineItems & machineAttachmentItems from the QuotationDto and not from QuotationMarginDto
  // because only those have the needed data for the check
  const hasArchivedItems = quoteHasArchivedItems(
    quote.machineItems,
    quote.machineAttachmentItems
  )

  // deny access due to empty quote
  if (hasNoMachines && hasNoAttachments) {
    return {
      denied: true,
      reason: AccessDeniedReason.EMPTY
    }
  }

  // deny access due to quote having item(s) with errors
  if (isInNeedsRevisingStatus) {
    return {
      denied: true,
      reason: AccessDeniedReason.INCOMPLETE
    }
  }

  // deny access due to having archived item(s) or archived brand(s)
  if (
    hasArchivedItems &&
    (quote.status === QuoteStatus.DRAFT_SENT_TO_CUSTOMER ||
      quote.status === QuoteStatus.DRAFT_REJECTED_BY_CUSTOMER ||
      quote.status === QuoteStatus.DRAFT_ACCEPTED_BY_CUSTOMER ||
      quote.status === QuoteStatus.DRAFT_ACCEPTED_ON_BEHALF_OF_CUSTOMER ||
      quote.status === QuoteStatus.LEVEL_1_APPROVED_PENDING_LEVEL_2_APPROVAL ||
      quote.status === QuoteStatus.PENDING_LEVEL_1_APPROVAL ||
      quote.status === QuoteStatus.PENDING_LEVEL_2_APPROVAL ||
      quote.status === QuoteStatus.REJECTED_INTERNAL_L1 ||
      quote.status === QuoteStatus.REJECTED_INTERNAL_L2 ||
      quote.status === QuoteStatus.ACCEPTED)
  ) {
    return {
      denied: true,
      reason: AccessDeniedReason.INCOMPLETE
    }
  }

  return {
    denied: false
  }
}

export const getIsQuoteEmpty = (quote: QuotationDto) => {
  return (
    quote.machineItems?.length === 0 &&
    quote.machineAttachmentItems?.length === 0
  )
}

export const getQuoteHasIncompleteItems = (quote: QuotationDto) => {
  return (
    !!quote.machineItems?.some(machine => machine.incomplete) ||
    !!quote.machineAttachmentItems?.some(attachment => attachment.incomplete)
  )
}

export const canArchiveQuote = (quoteStatus?: string | null) => {
  if (!quoteStatus) {
    return false
  }

  if (quoteStatus === QuoteStatus.ARCHIVED) {
    return false
  }

  return [
    QuoteStatus.DRAFT,
    QuoteStatus.NEEDS_REVISING,
    QuoteStatus.PRICE_CHANGED,
    QuoteStatus.ACCEPTED,
    QuoteStatus.DRAFT_SENT_TO_CUSTOMER,
    QuoteStatus.DRAFT_REJECTED_BY_CUSTOMER,
    QuoteStatus.DRAFT_ACCEPTED_BY_CUSTOMER,
    QuoteStatus.DRAFT_ACCEPTED_ON_BEHALF_OF_CUSTOMER,
    QuoteStatus.LEVEL_1_APPROVED_PENDING_LEVEL_2_APPROVAL,
    QuoteStatus.PENDING_LEVEL_1_APPROVAL,
    QuoteStatus.PENDING_LEVEL_2_APPROVAL,
    QuoteStatus.REJECTED_INTERNAL_L1,
    QuoteStatus.REJECTED_INTERNAL_L2
  ].includes(quoteStatus as QuoteStatus)
}

export const sortDealSummaryQuoteItems = (items: CombinedQuotationItems[]) => {
  let parentItems = []
  const childrenList: NestedQuoteItemsType = {}

  for (const item of items ?? []) {
    if (item.withinMachineItemId) {
      if (!childrenList[item.withinMachineItemId]) {
        childrenList[item.withinMachineItemId] = []
      }
      childrenList[item.withinMachineItemId].push(item)
    } else {
      parentItems.push(item)
    }
  }

  parentItems = orderBy(parentItems, 'orderIndex')

  const result = []

  for (const parent of parentItems) {
    result.push(parent)
    if (parent.quotationItemId && childrenList[parent.quotationItemId]) {
      result.push(...childrenList[parent.quotationItemId])
    }
  }

  return result
}

export const updateNestedAttachmentsPricesInMachines = (
  machineItems: MachineQuotationItemDto[],
  nestedAttachments: MachineItemNewPriceDto[]
) => {
  return machineItems.map(parentMachine => ({
    ...parentMachine,
    machineAttachments: parentMachine?.machineAttachments?.map(
      (attachment: MachineAttachmentItemDto) => {
        const match = nestedAttachments.find(
          element => element.quotationItemId === attachment.quotationItemId
        )
        return match
          ? {
              ...attachment,
              price: match.price,
              targetPrice: match.targetPrice
            }
          : attachment
      }
    )
  }))
}

export const updateMachinesAndAttachmentsPrices = ({
  oldMachinesList,
  oldAttachmentsList,
  newMachinesList,
  newAttachmentsList
}: {
  oldMachinesList: MachineQuotationItemDto[]
  oldAttachmentsList: MachineAttachmentItemDto[]
  newMachinesList: MachineItemNewPriceDto[]
  newAttachmentsList: MachineItemNewPriceDto[]
}) => {
  let machinesList = oldMachinesList
  let attachmentsList = oldAttachmentsList

  const oldAttachmentIds = attachmentsList.map(
    attachment => attachment.quotationItemId
  )

  const nestedAttachmentsList = newAttachmentsList.filter(
    newAttachment => !oldAttachmentIds.includes(newAttachment.quotationItemId)
  )

  if (newMachinesList?.length > 0) {
    machinesList = machinesList.map(oldMachine => {
      const updatedMachine = newMachinesList.find(
        newMachine => newMachine.quotationItemId === oldMachine.quotationItemId
      )
      return updatedMachine
        ? {
            ...oldMachine,
            price: updatedMachine.price,
            targetPrice: updatedMachine.targetPrice
          }
        : oldMachine
    })
  }

  if (newAttachmentsList?.length > 0) {
    attachmentsList = attachmentsList.map(oldAttachment => {
      const updatedAttachment = newAttachmentsList.find(
        newAttachment =>
          newAttachment.quotationItemId === oldAttachment.quotationItemId
      )
      return updatedAttachment
        ? {
            ...oldAttachment,
            price: updatedAttachment.price,
            targetPrice: updatedAttachment.targetPrice
          }
        : oldAttachment
    })
  }

  machinesList = updateNestedAttachmentsPricesInMachines(
    machinesList,
    nestedAttachmentsList
  )

  return {
    updatedMachinesList: machinesList,
    updatedAttachmentsList: attachmentsList
  }
}

export const extractParentMachineId = (
  quote: QuotationDto,
  attachmentId: string | number
) => {
  const parentMachine = quote.machineItems?.find(parentMachine =>
    parentMachine.machineAttachments?.some(
      attachment => attachment.id === attachmentId
    )
  )
  return parentMachine?.id ?? null
}

export const hasPriceChange = (
  localData: CombinedQuotationItems[],
  serverData: CombinedQuotationItems[]
) => {
  return (localData ?? []).some(localItem => {
    const serverItem = serverData.find(item => item.id === localItem.id)
    return (
      serverItem &&
      Math.round(Number(serverItem.sellPrice)) !==
        Math.round(Number(localItem.sellPrice))
    )
  })
}
